import React, { useEffect, useState } from "react";
import { Button, CircularProgress, Box, Alert } from "@mui/material";
import HealthAreaStep from "./HealthAreaStep";
import { IHealthAreaItem, handleUpdateHealthAreaCategory } from "../../../api/mosaic/health_area";
import ModalWrapper, { modalCancelButtonSx, modalSubmitButtonSx } from "../../shared/ModalWrapper";

interface EditHealthCategoryModalProps {
  healthAreaToEdit?: IHealthAreaItem;
  isOpen: boolean;
  handleClose: () => void;
  onUpdate: () => void;
}

const EditHealthCategoryModal: React.FC<EditHealthCategoryModalProps> = ({
  healthAreaToEdit,
  isOpen,
  handleClose,
  onUpdate,
}) => {
  const [formData, setFormData] = useState<{ name: string; icode: string }>({ name: "", icode: "" });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  useEffect(() => {
    if (healthAreaToEdit) {
      setFormData({ name: healthAreaToEdit.name || "", icode: healthAreaToEdit.icode || "" });
      setErrors({});
      setSubmissionError(null);
    }
  }, [healthAreaToEdit]);

  const handleSubmit = async () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name || formData.name.trim().length < 2) newErrors.name = "Name must be at least 2 characters.";
    if (!formData.icode || formData.icode.trim().length < 2) newErrors.icode = "Icode must be at least 2 characters.";
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }

    setIsSubmitting(true);
    setSubmissionError(null);
    try {
      await handleUpdateHealthAreaCategory(healthAreaToEdit!.id, formData.name, formData.icode);
      setIsSubmitting(false);
      onUpdate();
      handleClose();
    } catch (error: unknown) {
      setSubmissionError("Failed to update health area.");
      setIsSubmitting(false);
    }
  };

  return (
    <ModalWrapper
      open={isOpen}
      onClose={handleClose}
      title="Edit Health Area"
      actions={
        <>
          <Button variant="outlined" onClick={handleClose} disabled={isSubmitting} sx={modalCancelButtonSx}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
            sx={modalSubmitButtonSx}
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </>
      }
    >
      {submissionError && (
        <Box mb={2}>
          <Alert severity="error">{submissionError}</Alert>
        </Box>
      )}
      <HealthAreaStep
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        setErrors={setErrors}
      />
    </ModalWrapper>
  );
};

export default EditHealthCategoryModal;
